<template>
  <form>
    <div class="mb-6">
      <label for="dropzone" ref="image">
        Profile Photo
        <span class="mb-5 text-sm font-sans-book block">
          Please upload a horizontal/landscape image (JPEG or PNG), ideally 400 pixels x 300 pixels. This should represent you or your organization. If you are an individual representing the entire organization, you may use a photo of your face. Be sure to click Update at the bottom of the page once you have uploaded your photo.
        </span>
      </label>

      <div v-if="organization && organization.profilePhoto">
        <img :src="organization.profilePhoto" class="h-48 mb-2"  :alt="organization.name"/>
      </div>

      <vue-dropzone
        ref="image"
        id="dropzone"
        :options="dropzoneOptions"
        @vdropzone-success="uploaded"
        @vdropzone-error="updateImageErrorMessage"
        @vdropzone-removed-file="resetImage"
        :useCustomSlot=true
      >
        <div class="after-drop-message">
          <p>You may drag-and-drop an image here. File size must be less than 4 MB.</p>
        </div>
      </vue-dropzone>

      <span v-if="imageError" class="text-lg error text-error-red">
        {{ imageError }}
      </span>
    </div>

    <div class="mb-12">
      <label for="story" ref="story">
        What’s your organization’s story? <span class="text-red">*</span>
        <span class="text-sm font-sans-book block">
          900 characters maximum. This is your bio and description, which can include your mission statement, types of resources you provide, and/or what inspired you to do this work. Use I/we when you can; stories are better when they are in your personal voice!
        </span>
      </label>

      <textarea
        id="story"
        name="story"
        v-model="form.story"
        v-validate="'required|max:900'"
        class="w-full mt-3 p-4 overflow-y-scroll border border-black h-48 bg-transparent"
      />

      <div class="text-sm font-sans-italic text-gray-dark mt-1">
        Character count: {{ storyCount }}
      </div>

      <span v-show="errors.has('story')" class="error text-error-red text-lg">
        {{ errors.first('story') }}
      </span>
    </div>

    <div class="mb-12">
      <label for="reimagine" ref="reimagine">
        Finish the following: "What I'm / What we're here to reimagine..." <span class="text-red">*</span>
        <span class="text-sm font-sans-book block">
          240 characters maximum. What is your inspiring vision, mantra, or change you wish to see in the world? Keep this short, sweet, and unique.
        </span>
      </label>

      <textarea
        id="reimagine"
        name="reimagine"
        v-model="form.reimagine"
        v-validate="'required|max:240'"
        class="w-full mt-3 p-4 overflow-y-scroll border border-black h-48 bg-transparent"
      />

      <div class="text-sm font-sans-italic text-gray-dark mt-1">
        Character count: {{ reimagineCount }}
      </div>

      <span v-show="errors.has('reimagine')" class="error text-error-red text-lg">
        {{ errors.first('reimagine') }}
      </span>
    </div>

    <div class="mb-12">
      <label for="publicPhone">
        Public Phone Number
      </label>

      <input id="publicPhone" type="text" name="publicPhone" v-model="form.publicPhone" />
    </div>

    <div class="mb-12">
      <label for="publicEmail">
        Public Email
      </label>
      <input id="publicEmail" type="text" name="publicEmail" v-model="form.publicEmail" />
    </div>

    <div class="mb-12">
      <label for="facebookUrl">
        Facebook URL
      </label>

      <url-input id="facebookUrl" v-model="form.facebookUrl" />
    </div>

    <div class="mb-12">
      <label for="twitterUrl">
        Twitter URL
      </label>

      <url-input id="twitterUrl" v-model="form.twitterUrl" />
    </div>

    <div class="mb-12">
      <label for="instagramUrl">
        Instagram URL
      </label>

      <url-input id="instagramUrl" v-model="form.instagramUrl" />
    </div>

    <div class="mb-12">
      <label for="linkedinUrl">
        LinkedIn URL
      </label>

      <url-input id="linkedinUrl" v-model="form.linkedinUrl" />
    </div>

    <div class="w-full md:pl-10 md:w-3/4 md:pr-10">
      <div class="mb-8" v-for="group in taxonomyGroups" :key="group.id">
          <label ref="taxonomy">
              {{ group.title }} <span class="text-red">*</span>
          </label>
          <div class="group__checkbox">
              <div class="flex flex-wrap select-box">
                  <template v-for="taxonomy in group.taxonomies">
                      <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="taxonomy.id">
                          <input
                              name="taxonomy"
                              type="checkbox"
                              v-validate="'required'"
                              v-model="form.taxonomies"
                              :value="taxonomy.id"
                          />
                          <span class="inline-block mr-2 check"></span> {{ taxonomy.title }}
                      </label>
                  </template>
              </div>
          </div>
      </div>
      <span v-show="errors.has('taxonomy')" class="text-lg error text-error-red">{{ errors.first('taxonomy') }}</span>
    </div>

    <div class="mb-12" v-if="user.isAdmin">
      <div class="select-box">
        <label class="text-base font-normal" for="contractOverride">
          <input id="contractOverride" type="checkbox" v-model="form.contractOverride">
          <span class="check inline-block mr-2"></span> Override Festival Contract
        </label>
      </div>
    </div>

    <button class="button" @click.stop.prevent="update">
      Update
    </button>

    <div class="mb-3" v-if="organization && organization.id && user && user.isAdmin">
      <admin-queueton-status :resource-id="organization.id" resource="organization" />
    </div>

    <div class="mb-6" />

  </form>
</template>

<script>
import { mapGetters } from "vuex";
import urlFilter from "../../mixins/url-filter";
import UrlInput from "../global/UrlInput.vue";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import validation from "../../mixins/validation";
import AdminQueuetonStatus from "../admin/AdminQueuetonStatus.vue";

export default {
  name: "OrganizationEdit",
  title: "Organization Edit",
  mixins: [urlFilter, validation],
  components: {
    AdminQueuetonStatus,
    UrlInput,
    vueDropzone: vue2Dropzone
  },
  data: () => ({
    form: {
      organization_name: "",
      url: "",
      contractOverride: "",
      accountType: "",
      story: "",
      reimagine: "",
      publicPhone: "",
      newImage: "",
      publicEmail: "",
      city: "",
      state: "",
      facebookUrl: "",
      twitterUrl: "",
      instagramUrl: "",
      linkedinUrl: "",
      taxonomies: "",
    },
    imageError: ""
  }),
  computed: {
    ...mapGetters({
      organization: "organization",
      tokens: "tokens",
      user: "user",
      taxonomyGroups: "taxonomyGroups"
    }),
    dropzoneOptions() {
      return {
        addRemoveLinks: true,
        acceptedFiles: ".png,.jpg, image/png, image/jpeg",
        headers: {
          "X-Csrf-Token": document.head.querySelector(
            'meta[name="csrf-token"]'
          ).content,
          Authorization: "Bearer " + this.tokens.access_token
        },
        maxFiles: 1,
        maxFilesize: 4, // in Mb
        thumbnailWidth: "247",
        thumbnailHeight: "143",
        url: this.$laroute.route("api.temp-images.store"),
        withCredentials: true
      };
    },
    profileUrl() {
      return this.organization && this.organization.profileUrl
        ? this.organization.profileUrl
        : "";
    },
    reimagineCount() {
      return this.form.reimagine ? this.form.reimagine.length : 0;
    },
    storyCount() {
      return this.form.story ? this.form.story.length : 0;
    }
  },
  watch: {
    organization() {
      this.setForm();
    },

    user() {
      this.getOrganization();
    }
  },
  methods: {
    getOrganization() {
      let id;

      if (_.isEmpty(this.$route.params)) {
        id = this.user.organization_id;
      } else {
        id = this.$route.params.id;
      }

      this.$store.dispatch("getOrganization", { id: id });
    },
    resetImage() {
      this.imageError = "";

      axios
        .delete(this.$laroute.route("api.temp-images.destroy"), {
          data: { filePath: this.form.newImage }
        })
        .then(() => {
          this.form.newImage = "";
        })
        .catch(error => {
          this.displayError(error);
        });
    },
    setForm() {
      this.form.organization_name = this.organization.name;
      this.form.contractOverride = this.organization.contractOverride;
      this.form.accountType = this.organization.accountType;
      this.form.story = this.organization.story;
      this.form.reimagine = this.organization.reimagine;
      this.form.publicPhone = this.organization.publicPhone;
      this.form.publicEmail = this.organization.publicEmail;
      this.form.city = this.organization.city;
      this.form.state = this.organization.state;
      this.form.facebookUrl = this.organization.facebookUrl;
      this.form.twitterUrl = this.organization.twitterUrl;
      this.form.instagramUrl = this.organization.instagramUrl;
      this.form.linkedinUrl = this.organization.linkedinUrl;
      this.form.taxonomies = this.organization.taxonomies;

      const url = this.organization ? this.organization.url : "";
      this.form.url = this.filterUrl(url);
    },
    update() {
      this.$validator.validate().then(result => {
        if (!result) {
          this.gotoFirstValidationError();
          return;
        }

        axios
          .put(
            this.$laroute.route("api.organizations.update", {
              organization: this.organization.id
            }),
            this.form
          )
          .then(response => {
            this.$refs.image.removeAllFiles();

            this.$store.dispatch(
              "setActiveOrganization",
              response.data.data
            );
            this.$toasted.show(
              "Your public profile has been updated."
            );
          })
          .catch(error => {
            console.log(error);
            this.processing = false;

            if (error.response.data.errors) {
              _.forEach(
                error.response.data.errors,
                (message, id) => {
                  const field = this.$validator.fields.find({
                    name: id,
                    scope: this.$options.scope
                  });
                  this.$validator.errors.add({
                    id: field.id,
                    field: id,
                    msg: message[0],
                    scope: this.$options.scope
                  });
                }
              );
            }
          });
      });
    },
    updateImageErrorMessage(file, message) {
      this.imageError = message;
    },
    uploaded(file, path) {
      this.form.newImage = path;
    }
  },
  mounted() {
    this.getOrganization();
  }
};
</script>
